import React, { useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBInput,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useConfig } from '../config/ConfigProvider';
import { getFieldValidationRules } from '../validation/lectures';
import { DevContext } from '../../../../App';

export default function TitleStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { getStepContent } = useConfig();
  const { isDevMode } = useContext(DevContext);

  // Get validation info from schema
  const stepValidation = getStepValidation('title');
  const requiredFields = getStepRequiredFields('title');
  const titleFieldValidation = getFieldValidation('title');

  // Get content from configuration
  const stepHeading = getStepContent('title', 'heading');
  const stepDescription = getStepContent('title', 'description');

  // Get dynamic character limits from schema
  const titleValidationRules = eventConfig ? getFieldValidationRules(eventConfig, 'title', formData) : {};
  const MIN_CHARACTERS = titleValidationRules['text.minLength'] || 10;
  const MAX_CHARACTERS = titleValidationRules['text.maxLength'] || 60;

  // Initialize title array ensuring both languages exist
  const titles = React.useMemo(() => {
    const baseTitles = (Array.isArray(formData.title) && formData.title.length > 0) ? formData.title : [];
    const requiredLanguages = ['de', 'en'];
    const result = [...baseTitles];
    requiredLanguages.forEach(lang => {
      if (!result.find(t => t.language === lang)) {
        result.push({ language: lang, text: '' });
      }
    });
    return result;
  }, [formData.title]);

  const handleTitleChange = (language, value) => {
    const existingIndex = titles.findIndex(title => title.language === language);
    let updatedTitles;
    if (existingIndex >= 0) {
      updatedTitles = titles.map(title =>
        title.language === language
          ? { ...title, text: value }
          : title
      );
    } else {
      // Language entry doesn't exist yet, add it
      updatedTitles = [...titles, { language, text: value }];
    }
    handleFieldChange('title', updatedTitles);
  };

  const getTitleByLanguage = (language) => {
    const title = titles.find(t => t.language === language);
    return title ? title.text : '';
  };

  const getCharacterCount = (language) => {
    const text = getTitleByLanguage(language);
    return text.length;
  };

  const getRemainingChars = (language) => {
    const count = getCharacterCount(language);
    return Math.max(0, MIN_CHARACTERS - count);
  };

  const getExcessChars = (language) => {
    const count = getCharacterCount(language);
    return Math.max(0, count - MAX_CHARACTERS);
  };

  const isLanguageTitleValid = (language) => {
    const count = getCharacterCount(language);
    return count >= MIN_CHARACTERS && count <= MAX_CHARACTERS;
  };

  const handleContinue = () => {
    const isValid = titles.every(title => 
      title.text.length >= MIN_CHARACTERS && title.text.length <= MAX_CHARACTERS
    ) && stepValidation.isValid;
    
    if (isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const isValid = titles.every(title => 
    title.text.length >= MIN_CHARACTERS && title.text.length <= MAX_CHARACTERS
  ) && stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {stepHeading || t('Lecture Title')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {stepDescription || t('Please enter a descriptive title for your lecture in both German and English. Each title should be between {{min}} and {{max}} characters long.', { min: MIN_CHARACTERS, max: MAX_CHARACTERS })}
          </MDBTypography>
          
          {/* German Title */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <ReactCountryFlag 
                countryCode="DE" 
                svg 
                style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
              />
              <MDBTypography tag="h6" className="mb-0">
                {t('German Title')}
              </MDBTypography>
            </div>
            
            <MDBInput
              value={getTitleByLanguage('de')}
              onChange={(e) => handleTitleChange('de', e.target.value)}
              label={t('German Title')}
              type="text"
              maxLength={MAX_CHARACTERS}
              counter
            />
            
            <div className="form-text d-flex justify-content-between">
              <span>
                {getRemainingChars('de') > 0 && (
                  <span className="text-warning">
                    {t('{{count}} more characters needed', { count: getRemainingChars('de') })}
                  </span>
                )}
                {getExcessChars('de') > 0 && (
                  <span className="text-danger">
                    {t('{{count}} characters too many', { count: getExcessChars('de') })}
                  </span>
                )}
                {isLanguageTitleValid('de') && (
                  <span className="text-success">
                    ✓
                  </span>
                )}
              </span>
              <span className="text-muted">
                {getCharacterCount('de')}/{MAX_CHARACTERS}
              </span>
            </div>
          </div>
          
          {/* English Title */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <ReactCountryFlag 
                countryCode="US" 
                svg 
                style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
              />
              <MDBTypography tag="h6" className="mb-0">
                {t('English Title')}
              </MDBTypography>
            </div>
            
            <MDBInput
              value={getTitleByLanguage('en')}
              onChange={(e) => handleTitleChange('en', e.target.value)}
              label={t('English Title')}
              type="text"
              maxLength={MAX_CHARACTERS}
              counter
            />
            
            <div className="form-text d-flex justify-content-between">
              <span>
                {getRemainingChars('en') > 0 && (
                  <span className="text-warning">
                    {t('{{count}} more characters needed', { count: getRemainingChars('en') })}
                  </span>
                )}
                {getExcessChars('en') > 0 && (
                  <span className="text-danger">
                    {t('{{count}} characters too many', { count: getExcessChars('en') })}
                  </span>
                )}
                {isLanguageTitleValid('en') && (
                  <span className="text-success">
                    ✓
                  </span>
                )}
              </span>
              <span className="text-muted">
                {getCharacterCount('en')}/{MAX_CHARACTERS}
              </span>
            </div>
          </div>
        </MDBCol>
      </MDBRow>

      {hasFieldError('title') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('title')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Title Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Character Limits (from Schema):</strong> {MIN_CHARACTERS}-{MAX_CHARACTERS} characters
              </div>
              
              <div className="mb-2">
                <strong>Current Titles:</strong>
                <ul>
                  <li>DE: "{getTitleByLanguage('de')}" ({getCharacterCount('de')} characters)</li>
                  <li>EN: "{getTitleByLanguage('en')}" ({getCharacterCount('en')} characters)</li>
                </ul>
              </div>
              
              <div className="mb-2">
                <strong>Local Validation:</strong>{' '}
                <MDBBadge color={isValid ? 'success' : 'warning'}>
                  DE: {isLanguageTitleValid('de') ? '✓' : '✗'} | EN: {isLanguageTitleValid('en') ? '✓' : '✗'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {titleFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{titleFieldValidation.field}</code>: {titleFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={isValid ? 'success' : 'warning'}>
                  {isValid ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!isValid}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}